import { Disclosure } from '@headlessui/react';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  CloudUploadIcon,
} from '@heroicons/react/outline';
import { uploadFile } from 'api/v2/upload';
import analysingFilesAnimation from 'assets/Analysing-files-Web.json';
import { Button } from 'components/atoms/Button';
import FileExtensionBadge from 'components/atoms/FileExtensionBadge.component';
import { Modal } from 'components/atoms/Modal';
import UseEffect from 'components/atoms/UseEffect';
import FileUploadedItemV2 from 'components/molecules/FileUploadedList/FileUploadedItemV2.component';
import { OnboardingStepWrapper } from 'components/organisms/OnboardingStepLayout';
import { AnimatePresence, motion } from 'motion/react';
import useGetCurrentCustomerData from 'hooks/Authentication/useGetCurrentCustomerData';
import useInitiateAccountableBankingOnboarding from 'hooks/Bank/useInitiateAccountableBankingOnboarding';
import { useIntercom } from 'hooks/intercom.hook';
import { Fragment, useCallback, useState, type FC } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import Lottie from 'react-lottie';
import {
  WEBAPP_BANKING_ONBOARDING_AIDOCSCANNING_CLICKEDCONFIRMDOCISCORRECT,
  WEBAPP_BANKING_ONBOARDING_AIDOCSCANNING_CLICKEDCONTACTCOACHES,
  WEBAPP_BANKING_ONBOARDING_AIDOCSCANNING_CLICKEDTRYANOTHERDOC,
  WEBAPP_BANKING_ONBOARDING_AIDOCSCANNING_SUCCESS_CLICKEDNEXT,
} from 'utils/amplitude/events/bank.amplitude';
import { DocumentUpload, DocumentWithIssue } from 'utils/icons';
import {
  fadeTransition,
  globalyAcceptedFileUploaderTypes,
} from 'utils/constants';
import { getFileExtensionFromPath, getFileNameFromPath } from 'utils/helpers';
import { ExclamationCircleIcon } from 'utils/icons';
import { useAiMetadata } from 'contexts/AIMetadataProvider';
import { useShouldShowTaxAdvisor } from 'hooks/TaxAdvisor/useShouldShowTaxAdvisor';
import { LocalFileUploader } from 'components/organisms/FileUploader/FileUploader.component';
import { AvailableCountries } from 'types/users.types';
import type { FileUploadState } from 'hooks/filesUploadStoreFactory';
import type { ProgressSegment } from 'components/organisms/OnboardingStepLayout';
import type { ComponentType } from 'react';

interface FinanzamtDocUploaderProps {
  onUploaded?: (file: { url: string; payload: string; name?: string }) => void;
  onConfirm?: () => void;
  onSkip?: () => void;
  defaultFiles?: { url: string; payload: string; name?: string }[];
  isConfirming?: boolean;
  onboardingId?: string;
  files?: { url: string; payload: string; name?: string }[];
  setFiles?: (files: { url: string; payload: string; name?: string }[]) => void;
  // Wrapper props
  progressSegments?: ProgressSegment[];
  onProgressSegmentClick?: (segmentId: string) => void;
  title?: string;
  subtitle?: string;
  icon?: ComponentType<{ className?: string }>;
  showProgressBar?: boolean;
  showBackButton?: boolean;
  onBack?: () => void;
}

const FinanzamtDocUploader: FC<FinanzamtDocUploaderProps> = ({
  onUploaded,
  defaultFiles,
  onConfirm,
  onSkip,
  isConfirming,
  onboardingId,
  files,
  setFiles,
  progressSegments,
  onProgressSegmentClick,
  title,
  icon,
  showProgressBar,
  showBackButton = true,
  onBack,
}) => {
  const { t } = useTranslation();

  const user = useGetCurrentCustomerData().data;

  const numberOfFiles = files?.length || 0;

  const isGewerbetreibender = user?.accountType?.includes('gewerbetreibender');

  const accountableTermsUrl = t(
    `signup.footer.terms_conditions_url.${
      user?.country ?? AvailableCountries.germany
    }`,
  );

  const renderUploadedFileItem = useCallback(
    (props: FileUploadState) => (
      <FileUploadedItemV2
        name={props.file.name}
        size={props.file.size}
        status={props.status}
        onRetry={() => null}
        showProgress
        progress={props.progress}
      />
    ),
    [],
  );

  const [currentStep, setCurrentStep] = useState(0);

  const onboardingAPI = useInitiateAccountableBankingOnboarding();

  const { wholeDocumentIssues = [], documentIssues } =
    onboardingAPI?.data || {};

  const shouldShowTaxAdvisor = useShouldShowTaxAdvisor();
  const { showIntercom } = useIntercom();
  const { setIsHelperOpen } = useAiMetadata();

  const openChat = useCallback(() => {
    if (shouldShowTaxAdvisor) {
      setIsHelperOpen(true);
    } else {
      showIntercom();
    }
  }, [setIsHelperOpen, shouldShowTaxAdvisor, showIntercom]);

  const subtitleText = isGewerbetreibender
    ? t(
        'accountable_banking.onboarding.finanzamt_doc.subtitle.gewerbetreibender',
      )
    : t('accountable_banking.onboarding.finanzamt_doc.subtitle.freiberufler');

  const steps = [
    <motion.div
      {...fadeTransition}
      animate={{ opacity: 1, transition: { delay: 0.25 } }}
      key="upload"
    >
      <OnboardingStepWrapper
        showProgressBar={showProgressBar}
        progressSegments={progressSegments}
        onProgressSegmentClick={onProgressSegmentClick}
        title={title}
        subtitle={subtitleText}
        subtitleContainerClassName="[&_p+p]:mt-6"
        icon={icon ?? DocumentUpload}
        showBackButton={showBackButton}
        onBack={onBack}
      >
        <Modal
          name="proof of registration example image"
          width="5xl"
          trigger={(api) => (
            <button
              type="button"
              className="underline text-primary-700 text-base mb-5 w-full"
              onClick={api.openModal}
            >
              {t(
                'accountable_banking.onboarding.finanzamt_doc.see_example_file.cta',
              )}
            </button>
          )}
          render={() => (
            <img
              src={
                isGewerbetreibender
                  ? '/proof_registration_example.png'
                  : '/proof_registration_example_freelancer.png'
              }
              alt="proof of registration example"
            />
          )}
        />

        <div className="w-full px-2">
          <LocalFileUploader
            asDefault
            className="flex-col lg:flex-col w-full !gap-1"
            // todo: consider remove any type
            onUpload={(params: any) => {
              const originalName = params?.file?.name;
              // todo: consider remove any type
              return uploadFile(params).then((details: any) => {
                onUploaded?.({
                  url: details.key,
                  payload: details.payload,
                  name: originalName,
                });
              });
            }}
            filesListContainerClassName="!px-0"
            dropzoneProps={{
              className: 'flex-none lg:flex-1 !border-none !bg-transparent',
              renderWrapperClassName: '!p-0 !px-0 !py-0',
              color: 'bank',
              dropOptions: {
                accept: globalyAcceptedFileUploaderTypes,
              },
              render: () => (
                <div className="w-full overflow-hidden border border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center gap-5 py-8 px-6 transition-colors hover:bg-gray-50">
                  <CloudUploadIcon className="w-8 h-8 text-primary-400" />

                  <div className="text-center">
                    <p className="text-primary font-medium text-base">
                      {t(
                        'accountable_banking.onboarding.finanzamt_doc.uploader.title.de',
                      )}
                    </p>
                    <p className="text-primary-500 text-xs mt-1">
                      {t(
                        'accountable_banking.onboarding.finanzamt_doc.uploader.subtitle.de',
                      )}
                    </p>
                  </div>

                  <span className="text-primary-500 font-medium text-sm px-3 py-1 border border-gray-200 rounded-lg shadow-sm">
                    {t(
                      'accountable_banking.onboarding.finanzamt_doc.uploader.cta.de',
                    )}
                  </span>
                </div>
              ),
            }}
            renderUploadedFileItem={renderUploadedFileItem}
            filesListProps={{
              renderHeader: () => <div className="mb-4" />,
            }}
          />

          <p className="text-sm text-primary-400 mt-4">
            <Trans
              i18nKey="accountable_banking.onboarding.finanzamt_doc.terms"
              components={{
                termsLink: (
                  <a
                    href={accountableTermsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline"
                  />
                ),
              }}
            />
          </p>
        </div>

        <div className="w-full mt-6 space-y-3">
          <Button
            className="w-full"
            color="bank"
            disabled={!defaultFiles?.length || isConfirming}
            onClick={() => setCurrentStep(1)}
            loading={isConfirming}
          >
            {t('continue')}
          </Button>

          {onSkip && (
            <Button
              structure="text"
              className="w-full !text-primary-500"
              onClick={onSkip}
            >
              {t('skip_for_now')}
            </Button>
          )}
        </div>
      </OnboardingStepWrapper>
    </motion.div>,

    <motion.div {...fadeTransition} animate={{ opacity: 1 }} key="scanning">
      <UseEffect
        effect={() => {
          onboardingAPI.mutate({
            _id: onboardingId,
            filepaths: files?.map((file) => file.url),
            skipDocumentScan: false,
          });
        }}
        render={() => {
          if (onboardingAPI.isLoading) {
            return (
              <div className="flex flex-col items-center justify-center gap-y-2 h-full">
                <h1 className="relative !capitalize font-bold text-xl flex !bg-clip-text ai-gradient">
                  {t(
                    'accountable_banking.onboarding.analyzing_documents.title',
                  )}
                </h1>

                <p className="text-center text-sm text-primary-600">
                  {t(
                    'accountable_banking.onboarding.analyzing_documents.subtitle',
                  )}
                </p>

                <Lottie
                  options={{
                    animationData: analysingFilesAnimation,
                    loop: true,
                    autoplay: true,
                  }}
                  height={200}
                  width={200}
                />
              </div>
            );
          }

          if (wholeDocumentIssues?.length || documentIssues?.length) {
            const documentIssuesFlatCount =
              documentIssues?.reduce((acc, d) => acc + d.issues.length, 0) ?? 0;
            const totalIssues =
              (wholeDocumentIssues?.length ?? 0) + documentIssuesFlatCount;

            /**
             * Figma (Bank — document issues modal): compact file chip, “X issues” badge,
             * single flat accordion list, secondary “Continue with these documents” + primary “Try another document”.
             *
             * Deliberate product additions / retention (no equivalent in that frame):
             * - “Contact one of our coaches” + WEBAPP_BANKING_ONBOARDING_AIDOCSCANNING_CLICKEDCONTACTCOACHES
             * - Two-level grouping: whole-document issues vs per-file (“page”) issues — pre-existing UX
             * - “Skip doc scanning” text CTA — same confirm flow as the secondary button; kept for legacy copy
             */
            const renderIssueDisclosure = (issue: string) => (
              <Disclosure>
                {({ open }) => (
                  <>
                    <Disclosure.Button className="w-full flex items-center gap-2 px-4 py-3 text-left">
                      <ExclamationCircleIcon className="w-5 h-5 text-red-500 shrink-0" />
                      <span className="flex-1 text-sm font-medium text-primary">
                        {t(issue)}
                      </span>
                      {open ? (
                        <ChevronUpIcon className="w-5 h-5 text-primary-400 shrink-0" />
                      ) : (
                        <ChevronDownIcon className="w-5 h-5 text-primary-400 shrink-0" />
                      )}
                    </Disclosure.Button>

                    <AnimatePresence>
                      {open && (
                        <Disclosure.Panel
                          static
                          as={motion.div}
                          className="px-4 pb-3 text-sm text-gray-500"
                        >
                          {t(`${issue}.details`)}
                        </Disclosure.Panel>
                      )}
                    </AnimatePresence>
                  </>
                )}
              </Disclosure>
            );

            return (
              <OnboardingStepWrapper
                showProgressBar={showProgressBar}
                progressSegments={progressSegments}
                onProgressSegmentClick={onProgressSegmentClick}
                title={t(
                  'accountable_banking.onboarding.analyzing_documents.issues.title',
                  { numOfIssues: totalIssues },
                )}
                subtitle={
                  numberOfFiles === 1
                    ? t(
                        'accountable_banking.onboarding.analyzing_document.issues.subtitle',
                      )
                    : t(
                        'accountable_banking.onboarding.analyzing_documents.issues.subtitle',
                      )
                }
                icon={DocumentWithIssue}
                showBackButton
                onBack={() => {
                  setFiles?.([]);
                  setCurrentStep(0);
                }}
              >
                {/* Uploaded file cards (user-selected names when available) */}
                <div className="flex flex-wrap gap-1 justify-center mb-4">
                  {files?.map(({ url, name }) => (
                    <div
                      key={url}
                      className="border border-gray-200 rounded-lg shadow-sm p-2.5 flex items-center gap-2 self-center w-fit"
                    >
                      <FileExtensionBadge
                        extension={getFileExtensionFromPath(name || url)}
                      />
                      <span className="text-sm font-medium text-primary">
                        {name || getFileNameFromPath(url)}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="w-full pb-8 flex flex-col gap-6">
                  {wholeDocumentIssues?.length ? (
                    <div className="flex flex-col gap-2">
                      <h3
                        className="text-lg font-bold text-primary-800"
                        style={{ hyphens: 'none' }}
                      >
                        {t(
                          'accountable_banking.onboarding.finanzamt_doc.uploader.document_issues',
                        )}
                      </h3>
                      <div className="flex flex-col items-stretch">
                        <span className="inline-flex w-fit bg-red-100 text-red-700 text-xs font-medium px-2.5 py-1 rounded-t-lg relative z-0 -mb-2 pb-2.5">
                          {t(
                            'accountable_banking.onboarding.analyzing_documents.issues.badge',
                            {
                              count: totalIssues,
                              defaultValue: `${totalIssues} issues to review`,
                            },
                          )}
                        </span>
                        <div className="relative z-[1] bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
                          {wholeDocumentIssues.map((issue, index) => (
                            <Fragment key={`whole-doc-issue-${issue}-${index}`}>
                              {index > 0 ? (
                                <div className="h-px bg-gray-100" />
                              ) : null}
                              {renderIssueDisclosure(issue)}
                            </Fragment>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : null}

                  {documentIssues?.length ? (
                    <div className="flex flex-col gap-2">
                      <h3
                        className="text-lg font-bold text-primary-800"
                        style={{ hyphens: 'none' }}
                      >
                        {t(
                          'accountable_banking.onboarding.finanzamt_doc.uploader.page_issues',
                        )}
                      </h3>
                      <div className="flex flex-col gap-4">
                        {documentIssues.map(
                          ({ filepath, issues }, fileIndex) => (
                            <div key={filepath} className="space-y-2">
                              <div className="border border-gray-200 rounded-lg shadow-sm p-2.5 flex items-center gap-2 w-full">
                                <FileExtensionBadge
                                  extension={getFileExtensionFromPath(filepath)}
                                />
                                <span className="text-sm font-medium text-primary">
                                  {getFileNameFromPath(filepath)}
                                </span>
                              </div>
                              <div className="flex flex-col items-stretch">
                                {!wholeDocumentIssues?.length &&
                                fileIndex === 0 ? (
                                  <span className="inline-flex w-fit bg-red-100 text-red-700 text-xs font-medium px-2.5 py-1 rounded-t-lg relative z-0 mb-[-10px]">
                                    {t(
                                      'accountable_banking.onboarding.analyzing_documents.issues.badge',
                                      {
                                        count: totalIssues,
                                        defaultValue: `${totalIssues} issues to review`,
                                      },
                                    )}
                                  </span>
                                ) : null}
                                <div className="relative z-[1] bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
                                  {issues.map((issue, index) => (
                                    <Fragment
                                      key={`page-issue-${filepath}-${issue}-${index}`}
                                    >
                                      {index > 0 ? (
                                        <div className="h-px bg-gray-100" />
                                      ) : null}
                                      {renderIssueDisclosure(issue)}
                                    </Fragment>
                                  ))}
                                </div>
                              </div>
                            </div>
                          ),
                        )}
                      </div>
                    </div>
                  ) : null}
                </div>

                {/* Figma: secondary + primary row */}
                <div className="flex gap-2 w-full pb-5">
                  <Button
                    structure="secondary"
                    className="flex-1"
                    disabled={isConfirming}
                    loading={isConfirming}
                    onClick={onConfirm}
                    tracingEvent={
                      WEBAPP_BANKING_ONBOARDING_AIDOCSCANNING_CLICKEDCONFIRMDOCISCORRECT
                    }
                  >
                    {t(
                      'accountable_banking.onboarding.analyzing_documents.continue_with_docs',
                    )}
                  </Button>

                  <Button
                    className="flex-1"
                    color="bank"
                    disabled={isConfirming}
                    onClick={() => {
                      setFiles?.([]);
                      setCurrentStep(0);
                    }}
                    tracingEvent={
                      WEBAPP_BANKING_ONBOARDING_AIDOCSCANNING_CLICKEDTRYANOTHERDOC
                    }
                  >
                    {t(
                      'accountable_banking.onboarding.analyzing_documents.try_another',
                      {
                        defaultValue: 'Try another document',
                      },
                    )}
                  </Button>
                </div>

                {/* Not in Figma — support entry (CLICKEDCONTACTCOACHES) */}
                <Button
                  structure="text"
                  className="w-full"
                  disabled={isConfirming}
                  onClick={openChat}
                  tracingEvent={
                    WEBAPP_BANKING_ONBOARDING_AIDOCSCANNING_CLICKEDCONTACTCOACHES
                  }
                >
                  {t('contact_one_of_our_coaches')}
                </Button>
              </OnboardingStepWrapper>
            );
          }

          return (
            <OnboardingStepWrapper
              showProgressBar={showProgressBar}
              progressSegments={progressSegments}
              onProgressSegmentClick={onProgressSegmentClick}
              title={t(
                'accountable_banking.onboarding.analyzing_documents.success.title',
              )}
              subtitle={t(
                'accountable_banking.onboarding.analyzing_documents.success.subtitle',
              )}
              icon={icon ?? DocumentUpload}
            >
              {files?.map(({ url, name }) => (
                <div
                  key={url}
                  className="border border-gray-200 rounded-lg shadow-sm p-2.5 flex items-center gap-2 self-center"
                >
                  <FileExtensionBadge
                    extension={getFileExtensionFromPath(name || url)}
                  />
                  <span className="text-sm font-medium text-primary">
                    {name || getFileNameFromPath(url)}
                  </span>
                </div>
              ))}

              <Button
                className="w-full"
                color="bank"
                disabled={isConfirming}
                loading={isConfirming}
                onClick={onConfirm}
                tracingEvent={
                  WEBAPP_BANKING_ONBOARDING_AIDOCSCANNING_SUCCESS_CLICKEDNEXT
                }
              >
                {t('next')}
              </Button>
            </OnboardingStepWrapper>
          );
        }}
        deps={[user?.country, files, onboardingId]}
      />
    </motion.div>,
  ];

  return <AnimatePresence mode="wait">{steps[currentStep]}</AnimatePresence>;
};

export default FinanzamtDocUploader;
